import { Document, Packer, Paragraph, TextRun, ImageRun, AlignmentType, BorderStyle, TabStopType, LevelFormat, Table, WidthType, TableRow, TableCell } from 'docx';



export async function convertHtmlToDocx(htmlContent: string, logoBase64: string, blind = false): Promise<Blob> {
  const parser = new DOMParser();
  const doc2 = parser.parseFromString(htmlContent, 'text/html');

  const CONTENT_WIDTH = 10800;
  const pt = (n: number) => n * 2;

  function formatDate(date: string): string {
  // Replace every space with non-breaking space to prevent any wrapping
  return date.replace(/ /g, '\u00A0');
}

  function sectionHeader(text: string) {
    return new Paragraph({
      children: [new TextRun({ text, bold: true, size: pt(13), font: 'Calibri' })],
      border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: '000000', space: 1 } },
      spacing: { before: pt(7) * 10, after: pt(3) * 10 }
    });
  }

  function candidateName(text: string) {
    return new Paragraph({
      children: [new TextRun({ text, bold: true, size: pt(22), font: 'Calibri' })],
      alignment: AlignmentType.CENTER,
      spacing: { before: pt(6) * 10, after: pt(6) * 10 }
    });
  }

  function twoColRow(left: string, right: string, spaceBefore = 5, spaceAfter = 0, bold = true, size = 12) {
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [CONTENT_WIDTH - 3240, 3240],
    borders: {
      top: { style: BorderStyle.NONE, size: 0 },
      bottom: { style: BorderStyle.NONE, size: 0 },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
      insideHorizontal: { style: BorderStyle.NONE, size: 0 },
      insideVertical: { style: BorderStyle.NONE, size: 0 },
    },
    rows: [new TableRow({
      children: [
        new TableCell({
          width: { size: CONTENT_WIDTH - 3240, type: WidthType.DXA },
          borders: { top: { style: BorderStyle.NONE, size: 0 }, bottom: { style: BorderStyle.NONE, size: 0 }, left: { style: BorderStyle.NONE, size: 0 }, right: { style: BorderStyle.NONE, size: 0 } },
          children: [new Paragraph({
            children: [new TextRun({ text: left, bold, size: pt(size), font: 'Calibri' })],
            spacing: { before: spaceBefore * 20, after: spaceAfter * 20 }
          })]
        }),
        new TableCell({
          width: { size: 3240, type: WidthType.DXA },
          borders: { top: { style: BorderStyle.NONE, size: 0 }, bottom: { style: BorderStyle.NONE, size: 0 }, left: { style: BorderStyle.NONE, size: 0 }, right: { style: BorderStyle.NONE, size: 0 } },
          children: [new Paragraph({
            children: [new TextRun({ text: formatDate(right), bold, size: pt(size), font: 'Calibri' })],
            alignment: AlignmentType.RIGHT,
            spacing: { before: spaceBefore * 20, after: spaceAfter * 20 },
            keepLines: true,
            keepNext: true,
          })]
        })
      ]
    })]
  });
}

  function employerRow(employer: string, date: string) {
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [CONTENT_WIDTH - 3240, 3240],
    borders: {
      top: { style: BorderStyle.NONE, size: 0 },
      bottom: { style: BorderStyle.NONE, size: 0 },
      left: { style: BorderStyle.NONE, size: 0 },
      right: { style: BorderStyle.NONE, size: 0 },
      insideHorizontal: { style: BorderStyle.NONE, size: 0 },
      insideVertical: { style: BorderStyle.NONE, size: 0 },
    },
    rows: [new TableRow({
      children: [
        new TableCell({
          width: { size: CONTENT_WIDTH - 1440, type: WidthType.DXA },
          borders: { top: { style: BorderStyle.NONE, size: 0 }, bottom: { style: BorderStyle.NONE, size: 0 }, left: { style: BorderStyle.NONE, size: 0 }, right: { style: BorderStyle.NONE, size: 0 } },
          children: [new Paragraph({
            children: [new TextRun({ text: employer, bold: true, size: pt(12), font: 'Calibri' })],
            spacing: { before: pt(5) * 10, after: 0 }
          })]
        }),
        new TableCell({
          width: { size: 2160, type: WidthType.DXA },
          borders: { top: { style: BorderStyle.NONE, size: 0 }, bottom: { style: BorderStyle.NONE, size: 0 }, left: { style: BorderStyle.NONE, size: 0 }, right: { style: BorderStyle.NONE, size: 0 } },
          children: [new Paragraph({
          children: [new TextRun({ text: formatDate(date), bold: true, size: pt(12), font: 'Calibri' })],
          alignment: AlignmentType.RIGHT,
          spacing: { before: pt(5) * 10, after: 0 },
          keepLines: true,
          keepNext: true,
        })]
        })
      ]
    })]
  });
}

  function jobTitle(text: string, italic = false) {
    return new Paragraph({
      children: [new TextRun({ text, bold: true, italics: italic, size: pt(12), font: 'Calibri' })],
      spacing: { before: 0, after: 0 }
    });
  }

  function bulletPara(text: string) {
    return new Paragraph({
      numbering: { reference: 'seq-bullets', level: 0 },
      children: [new TextRun({ text, size: pt(11), font: 'Calibri' })],
      alignment: AlignmentType.BOTH,
      spacing: { before: pt(1) * 10, after: pt(1) * 10 }
    });
  }

  function skillLine(label: string, value: string) {
    return new Paragraph({
      children: [
        new TextRun({ text: label + ': ', bold: true, size: pt(11), font: 'Calibri' }),
        new TextRun({ text: value, size: pt(11), font: 'Calibri' }),
      ],
      spacing: { before: pt(1) * 10, after: pt(1) * 10 }
    });
  }

  function plainPara(text: string, bold = false) {
    return new Paragraph({
      children: [new TextRun({ text, bold, size: pt(11), font: 'Calibri' })],
      spacing: { before: pt(1) * 10, after: pt(1) * 10 }
    });
  }

  // Convert base64 logo to Uint8Array
  const logoBytes = Uint8Array.from(atob(logoBase64.split(',')[1]), c => c.charCodeAt(0));

  const children: any[] = [];

  // Only add SEQ header if not blind
  if (!blind) {
    // Logo
    children.push(new Paragraph({
      children: [new ImageRun({ data: logoBytes, transformation: { width: 213, height: 50 }, type: 'png' })],
      border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: '000000', space: 1 } },
      alignment: AlignmentType.LEFT,
      spacing: { before: 0, after: 0 }
    }));

    // Contact line
    children.push(new Paragraph({
      children: [new TextRun({ text: '1460 Broadway, New York, NY 10036 |talent@seqtechnology.com| Ph: 646-225-4039', size: pt(9), font: 'Calibri' })],
      border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: '000000', space: 1 } },
      spacing: { before: pt(2) * 10, after: 0 }
    }));
  }

  // Candidate name
  const nameEl = doc2.querySelector('div[style*="text-align: center"], h1');
  if (nameEl) children.push(candidateName(nameEl.textContent?.trim() || ''));

  // Sections
  const sectionEls = doc2.querySelectorAll('div[style*="border-bottom"]');
  sectionEls.forEach(sec => {
    const secText = sec.textContent?.trim() || '';
    children.push(sectionHeader(secText));

    let el = sec.nextElementSibling;
    while (el) {
      const style = el.getAttribute('style') || '';
      if (style.includes('border-bottom')) break;

      const text = el.textContent?.trim() || '';
      if (!text) { el = el.nextElementSibling; continue; }

      // Education flex div rows — must come before employer row check
      if (style.includes('display: flex') && (
        style.includes('margin-top: 5pt') ||
        style.includes('margin-top: 10pt') ||
        style.includes('margin-top: 0pt')
      )) {
        const spans = el.querySelectorAll('span');
        const spaceBefore = style.includes('margin-top: 10pt') ? 10 : style.includes('margin-top: 5pt') ? 5 : 0;
        if (spans.length >= 2) {
          children.push(twoColRow(
            spans[0].textContent?.trim() || '',
            spans[1].textContent?.trim() || '',
            spaceBefore, 0, true, 12
          ));
        } else if (spans.length === 1) {
          children.push(twoColRow(spans[0].textContent?.trim() || '', '', spaceBefore, 0, true, 12));
        }
        el = el.nextElementSibling;
        continue;
      }

      // Employer row
      if (style.includes('margin-top: 5pt')) {
        const tds = el.querySelectorAll('td');
        if (tds.length >= 2) {
          children.push(employerRow(tds[0].textContent?.trim() || '', tds[1].textContent?.trim() || ''));
        }
        el = el.nextElementSibling;
        continue;
      }

      // Job title
      if (style.includes('font-size: 12pt') && style.includes('font-weight: bold')) {
        children.push(jobTitle(text, style.includes('italic')));
        el = el.nextElementSibling;
        continue;
      }

      // Bullet tables
      if (el.tagName === 'TABLE') {
        el.querySelectorAll('td:last-child').forEach(td => {
          const btext = td.textContent?.trim() || '';
          if (btext) children.push(bulletPara(btext));
        });
        el = el.nextElementSibling;
        continue;
      }

      // Skills
      if (secText === 'TECHNICAL SKILLS') {
        const colonIdx = text.indexOf(':');
        if (colonIdx > -1) {
          children.push(skillLine(text.substring(0, colonIdx), text.substring(colonIdx + 1).trim()));
        } else {
          children.push(plainPara(text));
        }
        el = el.nextElementSibling;
        continue;
      }

      // Everything else
      children.push(plainPara(text, style.includes('font-weight: bold')));
      el = el.nextElementSibling;
    }
  });

  const document = new Document({
    numbering: {
      config: [{
        reference: 'seq-bullets',
        levels: [{
          level: 0,
          format: LevelFormat.BULLET,
          text: '\u25AA',
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: { indent: { left: 360, hanging: 260 } },
            run: { font: 'Calibri', size: pt(11) }
          }
        }]
      }]
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 720, right: 720, bottom: 720, left: 720 }
        }
      },
      children
    }]
  });

  return await Packer.toBlob(document);
}